import { useState } from "react";
import { ChevronDown, MapPin, Check, ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { airports } from "@/data/airports";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

interface AirportSwitcherProps {
  currentAirport: string;
  currentTerminal: string;
  onAirportChange: (airport: string, terminal: string) => void;
}

const AirportSwitcher = ({ 
  currentAirport, 
  currentTerminal,
  onAirportChange 
}: AirportSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const getAirportCode = (airportString: string | undefined | null) => {
    if (typeof airportString !== "string" || !airportString) return "";
    const match = airportString.match(/^([A-Z]{3})/);
    return match ? match[1] : airportString;
  };
  
  const currentCode = getAirportCode(currentAirport);
  const currentAirportData = airports.find(a => a.code === currentCode);

  const handleSelect = (airport: typeof airports[0]) => {
    onAirportChange(
      `${airport.code} - ${airport.name}`,
      currentTerminal
    );
    setOpen(false);
  };

  const triggerButton = (
    <Button 
      variant="ghost" 
      className="h-auto py-2 px-3 flex items-center gap-2 hover:bg-muted/50 min-h-[48px]"
    >
      <div className="p-2 bg-primary/10 rounded-lg">
        <MapPin className="h-4 w-4 text-primary" />
      </div>
      <div className="text-left">
        <p className="text-base font-semibold text-foreground leading-tight">
          {currentCode}
        </p>
        <p className="text-xs text-muted-foreground">
          Terminal {currentTerminal}
        </p>
      </div>
      <ChevronDown className="h-4 w-4 text-muted-foreground ml-1" />
    </Button>
  );

  const airportList = (
    <Command>
      <CommandInput placeholder="Search airports..." className="h-14 text-base" />
      <CommandList className={isMobile ? "max-h-[60vh]" : "max-h-[300px]"}>
        <CommandEmpty>No airport found.</CommandEmpty>
        
        {/* Change airport & terminal button */}
        <div className="px-3 py-2 border-b border-border">
          <Button
            variant="outline"
            size="sm"
            className="w-full min-h-[44px] gap-2 text-sm"
            onClick={() => {
              setOpen(false);
              navigate("/home");
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            Change Airport & Terminal
          </Button>
        </div>

        <CommandGroup heading="All Airports" className="px-2">
          {airports.map((airport) => (
            <CommandItem
              key={airport.code}
              value={`${airport.code} ${airport.name} ${airport.city}`}
              onSelect={() => handleSelect(airport)}
              className="min-h-[56px] py-3 cursor-pointer"
            >
              <Check
                className={cn(
                  "mr-3 h-4 w-4 flex-shrink-0",
                  currentCode === airport.code ? "opacity-100" : "opacity-0"
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base text-foreground">{airport.code}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {airport.name} · {airport.city}
                </p>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          {triggerButton}
        </DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-lg">Switch Airport</DrawerTitle>
          </DrawerHeader>
          {airportList}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        {airportList}
      </PopoverContent>
    </Popover>
  );
};

export default AirportSwitcher;
